// client/src/pages/EmailVerification/index.jsx - דף אימות מייל חדש
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  verifyEmailAndRegister,
  resendVerificationCode,
  checkVerificationStatus,
} from "../../api/userApi";

const EmailVerification = () => {
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [canResend, setCanResend] = useState(false);
  const [resendCount, setResendCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  // קבלת המייל מ-props או מ-localStorage
  useEffect(() => {
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem("pendingVerificationEmail");

    if (emailFromState) {
      setEmail(emailFromState);
    } else if (emailFromStorage) {
      setEmail(emailFromStorage);
    } else {
      // אם אין מייל, חזרה לדף הרישום
      navigate("/user/register");
      return;
    }

    // טעינת סטטוס אימות
    loadVerificationStatus();
  }, [location.state, navigate]);

  // טיימר לספירה לאחור
  useEffect(() => {
    let interval;
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timeLeft]);

  // פוקוס על התא הראשון
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const loadVerificationStatus = async () => {
    if (!email) return;

    try {
      const response = await checkVerificationStatus(email);
      if (response.success) {
        setAttemptsLeft(response.status.attemptsLeft);
        setCanResend(response.status.canResendCode);
        setTimeLeft(response.status.timeUntilResend);
        setResendCount(response.status.resendCount);

        if (!response.status.isValid) {
          setError("בקשת האימות פגה תוקף או חרגת ממספר הניסיונות");
        }
      }
    } catch (error) {
      console.error("Error loading verification status:", error);
    }
  };

  const handleInputChange = (index, value) => {
    // רק ספרות
    if (!/^\d*$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // ניקוי שגיאות
    if (error) setError("");
    if (success) setSuccess("");

    // מעבר לתא הבא
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // שליחה אוטומטית כשמסיימים הזנה
    const fullCode = newCode.join("");
    if (fullCode.length === 6) {
      handleVerification(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace - מעבר לתא הקודם
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Enter - שליחה ידנית
    if (e.key === "Enter") {
      const fullCode = verificationCode.join("");
      if (fullCode.length === 6) {
        handleVerification(fullCode);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // רק ספרות באורך 6
    if (!/^\d{6}$/.test(pastedData)) {
      setError("יש להדביק קוד בן 6 ספרות");
      return;
    }

    const newCode = pastedData.split("");
    setVerificationCode(newCode);

    // פוקוס על התא האחרון
    inputRefs.current[5]?.focus();

    // שליחה אוטומטית
    handleVerification(pastedData);
  };

  const handleVerification = async (code) => {
    if (loading) return;

    const codeToUse = code || verificationCode.join("");

    if (codeToUse.length !== 6) {
      setError("יש להזין קוד בן 6 ספרות");
      return;
    }

    setLoading(true);
    setError("");

    try {
const response = await verifyEmailAndRegister(email, codeToUse);

      if (response.success) {
        setSuccess("האימות הצליח! המשתמש נוצר בהצלחה");

        // ניקוי localStorage
        localStorage.removeItem("pendingVerificationEmail");

        // מעבר לדף הצלחה
        setTimeout(() => {
          navigate("/user/registration-success", {
            state: {
              user: response.user,
              message: response.message,
              nextSteps: response.nextSteps,
            },
          });
        }, 2000);
      } else {
        setError(response.message);
        setAttemptsLeft(response.attemptsLeft || attemptsLeft - 1);

        // איפוס הקוד אם שגוי
        setVerificationCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Verification error:", error);
      if (error.response?.status === 400) {
        setError(error.response.data?.message || "קוד אימות שגוי");
        setAttemptsLeft(error.response.data?.attemptsLeft || attemptsLeft - 1);
      } else {
        setError("שגיאה בתהליך האימות. נסה שוב");
      }

      // איפוס הקוד
      setVerificationCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendLoading || !canResend || resendCount >= 3) return;

    setResendLoading(true);
    setError("");

    try {
      const response = await resendVerificationCode(email);

      if (response.success) {
        setSuccess("קוד אימות חדש נשלח בהצלחה");
        setTimeLeft(60); // דקה המתנה
        setCanResend(false);
        setResendCount(response.resendCount || resendCount + 1);

        // איפוס הקוד הקיים
        setVerificationCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        setError(response.message);
        setTimeLeft(response.timeLeft || 60);
      }
    } catch (error) {
      console.error("Resend error:", error);
      if (error.response?.status === 429) {
        const data = error.response.data;
        setError(data.message);
        setTimeLeft(data.timeLeft || 60);
      } else {
        setError("שגיאה בשליחת קוד חדש");
      }
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "20px",
          padding: "40px",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          textAlign: "center",
          direction: "rtl",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <div style={{ fontSize: "3rem", marginBottom: "10px" }}>📧</div>
          <h1
            style={{
              fontSize: "1.8rem",
              color: "#2c3e50",
              marginBottom: "10px",
              fontWeight: "600",
            }}
          >
            אימות כתובת מייל
          </h1>
          <p
            style={{
              color: "#7f8c8d",
              fontSize: "1rem",
              lineHeight: "1.5",
            }}
          >
            הזן את קוד האימות בן 6 הספרות
            <br />
            שנשלח אל: <strong style={{ color: "#2c3e50" }}>{email}</strong>
          </p>
        </div>

        {/* הודעות */}
        {error && (
          <div
            style={{
              background: "#ffebee",
              color: "#c62828",
              padding: "12px 16px",
              borderRadius: "10px",
              marginBottom: "20px",
              border: "1px solid #ffcdd2",
              fontSize: "0.95rem",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              background: "#e8f5e8",
              color: "#2e7d32",
              padding: "12px 16px",
              borderRadius: "10px",
              marginBottom: "20px",
              border: "1px solid #c8e6c9",
              fontSize: "0.95rem",
            }}
          >
            {success}
          </div>
        )}

        {/* שדות הקוד */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            marginBottom: "30px",
            direction: "ltr",
          }}
        >
          {verificationCode.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              maxLength={1}
              disabled={loading}
              style={{
                width: "50px",
                height: "50px",
                fontSize: "1.5rem",
                textAlign: "center",
                border: `2px solid ${digit ? "#667eea" : "#e0e6ed"}`,
                borderRadius: "10px",
                outline: "none",
                transition: "all 0.2s ease",
                background: loading ? "#f5f5f5" : "white",
                color: "#2c3e50", // ← הוסף את זה
                fontWeight: "600", // ← אופציונלי - יעשה את הטקסט יותר בולט
                cursor: loading ? "not-allowed" : "text",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#667eea";
                e.target.style.boxShadow = "0 0 0 3px rgba(102,126,234,0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = digit ? "#667eea" : "#e0e6ed";
                e.target.style.boxShadow = "none";
              }}
            />
          ))}
        </div>

        {/* מידע נוסף */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "15px",
            borderRadius: "10px",
            marginBottom: "20px",
            fontSize: "0.9rem",
            color: "#6c757d",
          }}
        >
          <div style={{ marginBottom: "8px" }}>
            <strong>נסיונות נותרים:</strong> {attemptsLeft}
          </div>
          <div>
            <strong>שליחות מחדש נותרות:</strong> {Math.max(0, 3 - resendCount)}
          </div>
        </div>

        {/* כפתור שליחה ידנית */}
        {verificationCode.join("").length === 6 && (
          <button
            onClick={() => handleVerification()}
            disabled={loading}
            style={{
              width: "100%",
              padding: "12px",
              background: loading
                ? "#95a5a6"
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: "20px",
              transition: "all 0.2s ease",
            }}
          >
            {loading ? "מאמת..." : "אמת קוד"}
          </button>
        )}

        {/* כפתור שליחה מחדש */}
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {canResend && resendCount < 3 ? (
            <button
              onClick={handleResendCode}
              disabled={resendLoading}
              style={{
                background: "none",
                border: "2px solid #667eea",
                color: "#667eea",
                padding: "10px 20px",
                borderRadius: "10px",
                cursor: resendLoading ? "not-allowed" : "pointer",
                fontSize: "0.95rem",
                fontWeight: "500",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!resendLoading) {
                  e.target.style.background = "#667eea";
                  e.target.style.color = "white";
                }
              }}
              onMouseLeave={(e) => {
                if (!resendLoading) {
                  e.target.style.background = "none";
                  e.target.style.color = "#667eea";
                }
              }}
            >
              {resendLoading ? "שולח..." : "שלח קוד חדש"}
            </button>
          ) : (
            timeLeft > 0 && (
              <div
                style={{
                  color: "#7f8c8d",
                  fontSize: "0.9rem",
                  padding: "10px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                שליחה מחדש תהיה זמינה בעוד:{" "}
                <strong>{formatTime(timeLeft)}</strong>
              </div>
            )
          )}

          {resendCount >= 3 && (
            <div
              style={{
                color: "#e74c3c",
                fontSize: "0.9rem",
                padding: "10px",
                background: "#ffebee",
                borderRadius: "8px",
                border: "1px solid #ffcdd2",
              }}
            >
              חרגת ממספר השליחות המותר. פנה למנהל המערכת.
            </div>
          )}
        </div>

        {/* קישורים */}
        <div
          style={{
            marginTop: "30px",
            borderTop: "1px solid #e9ecef",
            paddingTop: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <button
            onClick={() => navigate("/user/register")}
            style={{
              background: "none",
              border: "none",
              color: "#667eea",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            חזור לדף הרישום
          </button>

          <button
            onClick={() => navigate("/user/login")}
            style={{
              background: "none",
              border: "none",
              color: "#6c757d",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            יש לי כבר חשבון - התחבר
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
